'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { UserCircle2, Sparkles } from 'lucide-react';
import { Link, useRouter, usePathname } from '@/i18n/routing';
import { api } from '@/lib/api';

export default function Header() {
  const t = useTranslations('header');
  const router = useRouter();
  const pathname = usePathname();
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api
      .me()
      .then((u) => {
        setIsAdmin(u.role === 'admin');
        // 會員顯示暱稱;admin/客服直接顯示帳號名稱。都無則用 Email 前段。
        const account = u.email.split('@')[0];
        setName(u.role === 'admin' ? account : u.display_name || account);
      })
      .catch(() => setIsAdmin(false));
    // Re-check on every navigation: the layout (and this component) persists
    // across client-side nav, so without this an admin→member account switch
    // leaves isAdmin stale-true and hides the member's header.
  }, [pathname]);

  function ask(e: React.FormEvent) {
    e.preventDefault();
    const text = q.trim();
    if (!text) return;
    setQ('');
    router.push(`/stocks?q=${encodeURIComponent(text)}`);
  }

  // No consumer header on the login screen or the admin dashboard (which has
  // its own chrome). The /admin path check is synchronous — no me() flash.
  if (pathname === '/login' || pathname === '/reset' || pathname.startsWith('/admin') || isAdmin)
    return null;

  return (
    <header className="fixed inset-x-0 top-0 z-30 border-b border-zinc-200 bg-white/85 backdrop-blur-lg dark:border-zinc-800 dark:bg-zinc-900/85">
      <div className="mx-auto flex h-14 max-w-md items-center gap-2 px-3">
        {/* Left: avatar + 暱稱 → 個人設置;微晃動提示可點 */}
        <Link
          href="/settings"
          aria-label="個人設置"
          className="flex shrink-0 items-center gap-1 rounded-full py-1 pr-1 text-zinc-600 transition-all active:scale-95 active:text-brand dark:text-zinc-300 dark:active:text-brand"
        >
          <UserCircle2 size={28} strokeWidth={1.8} className="animate-wiggle" />
          {name && <span className="max-w-[72px] truncate text-sm font-medium">{name}</span>}
        </Link>

        {/* Center: quick-ask AI */}
        <form onSubmit={ask} className="flex flex-1 items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 transition-colors focus-within:border-brand/60 focus-within:bg-white dark:border-zinc-700 dark:bg-zinc-800 dark:focus-within:border-brand/50 dark:focus-within:bg-zinc-900">
          <Sparkles size={15} className="shrink-0 text-brand" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('askPlaceholder')}
            className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-400"
            enterKeyHint="search"
          />
        </form>
      </div>
    </header>
  );
}
